import { Component } from "react"

/* Redux */
import { connect } from "react-redux"

/* Components */
import { InnerLayout, Pagination, Avatar } from "../../components"

/* Widgets */
import { Filter, Detail, JackpotModal } from "../../widgets/Bonus"

/* REST API */
import { list } from "../../api/Bonus"

/* Helpers */
import { withRouter, utils } from "../../helpers"

/* Access */
import { isSuperAdmin } from "../../constants/access"

/* Icons */
import { SearchIcon, CloseIcon, FilterIcon, EyeIcon, CopyIcon, GiftIcon, TrophyIcon, ChipIcon, JackpotIcon, PlayerIcon, LayersIcon, NetIcon, StudioIcon, OperatorIcon, HashIcon, CardsIcon, SettingsIcon, VipIcon } from "../../widgets/Bonus/BonusIcons"

/* Utils */
import { getGame, getName, getAvatar, getCurrency, isWon, getWinUSD, getJackpotPart, formatCount, formatDay, formatTime, formatAgo } from "../../widgets/Bonus/BonusUtils"

/* Translate */
import { t } from "../../translates/translate"

/* Sweet alert */
import Swal from "../../helpers/alert"

/* Moment */
import moment from "moment"


/* PAGE SIZE — THE SAME NUMBER THE CONTROLLER USES */
const SIZE = 20

/* DELAY BEFORE THE SEARCH REQUEST LEAVES */
const DELAY = 400

/* EMPTY ANSWER — THE SAME SHAPE THE CONTROLLER SENDS */
const EMPTY = {
    totals: { bonus: 0, win: 0, jackpot: 0, cash: 0 },
    stats: { players: 0, wins: 0, games: 0, max: 0 },
}


/* Page Bonus */
class Bonus extends Component {

    constructor() {
        super()

        this.state = {
            data: [],
            status: "loading",
            count: 0,
            search: "",
            operators: [],
            tables: [],
            selected: "all",
            start: null,
            end: null,
            result: "",
            pool: "",
            testers: false,
            vips: true,
            totals: EMPTY.totals,
            stats: EMPTY.stats,
            settings: false,
        }
    }

    componentDidMount = () => {
        window.scrollTo(0, 0)
        this.load()
    }

    componentDidUpdate = (prevProps) => {

        /* Fields */
        const { params } = this.props
        const { page } = params

        /* Check Page — a new page starts from the top with the loader */
        if (prevProps.params.page !== page) {
            window.scrollTo(0, 0)
            this.load(true)
        }

    }

    componentWillUnmount = () => {
        clearTimeout(this._timer)
    }

    /* Get Page */
    getPage = () => {

        /* Fields */
        const { params } = this.props
        const { page } = params

        /* Check Page */
        if (Number.isInteger(parseInt(page))) {
            return parseInt(page)
        }

        return 1
    }

    /* Check Superadmin — the jackpot of an operator belongs to the superadmin alone */
    isSuperAdmin = () => {

        /* Fields */
        const { admin } = this.props

        /* Send Result */
        return isSuperAdmin(admin)
    }

    /* Load Action */
    load = (reload = false) => {

        /* Fields */
        const { search, operators, tables, start, end, result, pool, testers, vips } = this.state
        const page = this.getPage()

        /* Check Reload */
        if (reload) {
            this.setState({ status: "loading" })
        }

        /* Send to REST API */
        list({
            page,
            search,
            operators: operators.map((item) => item.id).join(","),
            tables: tables.map((item) => item.id).join(","),
            start: start ? moment(start).format("YYYY-MM-DD HH:mm:ss") : "",
            end: end ? moment(end).format("YYYY-MM-DD HH:mm:ss") : "",
            result,
            pool,
            testers,
            vips,
        }).then((response) => {
            if (response.status === 200) {
                this.setState({
                    status: "ok",
                    data: response.data.list,
                    count: response.data.count,
                    totals: response.data.totals ? response.data.totals : EMPTY.totals,
                    stats: response.data.stats ? response.data.stats : EMPTY.stats,
                })
            }
            else if (response.status === 401 || response.status === 403) {
                this.setState({ status: "permission", data: [], count: 0, ...EMPTY })
            }
            else if (response.status === 498) {
                this.setState({ status: "key", data: [], count: 0, ...EMPTY })
            }
            else {
                this.setState({ status: "error", data: [], count: 0, ...EMPTY })
            }
        }).catch(() => {
            this.setState({ status: "network", data: [], count: 0, ...EMPTY })
        })

    }

    /* Change Search — the request leaves only after the typing stops */
    changeSearch = (search) => {

        this.setState({ search })

        clearTimeout(this._timer)
        this._timer = setTimeout(() => this.apply(), DELAY)
    }

    /* Apply Filter */
    applyFilter = ({ selected, start, end, operators, tables, result, pool, testers, vips }) => {
        this.setState({ selected, start, end, operators, tables, result, pool, testers, vips }, () => this.apply())
    }

    /* Apply Action — every filter restarts from the first page */
    apply = () => {

        /* Fields */
        const { navigate } = this.props

        /* Check Page */
        if (this.getPage() !== 1) {
            navigate("/bonus/1")
            return
        }

        this.load()
    }

    /* Copy Action — the number of a game goes to the clipboard straight from the row */
    copy = (value) => {

        /* Check Value */
        if (!value) {
            Swal.fire(utils.notification(t("bns.copyFailed"), "error"))
            return
        }

        /* Check Clipboard — an insecure origin has none */
        if (!navigator.clipboard) {
            Swal.fire(utils.notification(t("bns.copyFailed"), "error"))
            return
        }

        navigator.clipboard.writeText(String(value)).then(() => {
            Swal.fire(utils.notification(t("bns.copied"), "success"))
        }).catch(() => {
            Swal.fire(utils.notification(t("bns.copyFailed"), "error"))
        })

    }

    /* Count Of The Active Filters — the badge of the filter button */
    getCount = () => {

        /* Fields */
        const { selected, operators, tables, result, testers, vips } = this.state
        let count = 0

        if (selected !== "all") {
            count = count + 1
        }

        if (operators.length > 0) {
            count = count + 1
        }

        if (tables.length > 0) {
            count = count + 1
        }

        if (result !== "") {
            count = count + 1
        }

        if (!testers) {
            count = count + 1
        }

        if (!vips) {
            count = count + 1
        }

        /* Send Result */
        return count
    }

    /* Draw Search */
    _search = () => {

        /* Fields */
        const { search } = this.state

        return (
            <div className="bns--search">

                <SearchIcon />

                <input
                    value={search}
                    onChange={(event) => this.changeSearch(event.target.value)}
                    placeholder={t("bns.search")}
                />

                {search !== "" &&
                    <div onClick={() => this.changeSearch("")} className="bns--search-clear">
                        <CloseIcon size={12} />
                    </div>
                }

            </div>
        )
    }

    /* Draw Filter Button */
    _filter = () => {

        /* Fields */
        const { selected, start, end, operators, tables, result, pool, testers, vips } = this.state
        const count = this.getCount()

        return (
            <div
                onClick={() => this._filterModal.open({ selected, start, end, operators, tables, result, pool, testers, vips })}
                className={`bns--filter ${count > 0 ? "active" : ""}`}
            >
                <FilterIcon size={15} />
                <span>{t("bns.filter")}</span>
                {count > 0 && <div className="bns--filter-count">{count}</div>}
            </div>
        )
    }

    /* Draw Settings Button — the jackpot of the operators, the superadmin alone opens it */
    _settings = () => {

        /* Check Access */
        if (!this.isSuperAdmin()) {
            return null
        }

        return (
            <div onClick={() => this.setState({ settings: true })} className="bns--settings">
                <SettingsIcon size={15} />
                <span>{t("bns.settings")}</span>
            </div>
        )
    }

    /* Draw One Number Of The Filter */
    _stat = (icon, title, value, tone = "") => {

        return (
            <div className={`bns--stat ${tone}`}>

                <div className="bns--stat-icon">{icon}</div>

                <div className="bns--stat-text">
                    <div className="bns--stat-title">{title}</div>
                    <div className="bns--stat-value">{value}</div>
                </div>

            </div>
        )
    }

    /* Draw Numbers — what the current filter really holds */
    _numbers = () => {

        /* Fields */
        const { count, stats } = this.state

        return (
            <div className="bns--stats">

                {this._stat(<LayersIcon />, t("bns.total"), formatCount(count), "blue")}
                {this._stat(<PlayerIcon />, t("bns.playersCount"), formatCount(stats.players), "violet")}
                {this._stat(<TrophyIcon />, t("bns.winsCount"), formatCount(stats.wins), "green")}
                {this._stat(<ChipIcon />, t("bns.gamesCount"), formatCount(stats.games), "mint")}
                {this._stat(<JackpotIcon />, t("bns.maxWin"), utils.convertor(stats.max, "USD", 1), "gold")}

            </div>
        )
    }

    /* Draw Money — the sums of the whole filter, in USD */
    _summary = () => {

        /* Fields */
        const { totals, stats } = this.state
        const bonus = Number(totals.bonus) || 0
        const win = Number(totals.win) || 0
        const net = bonus - win

        return (
            <div className="bns--summary">

                {/* Bets */}
                <div className="bns--card bet">
                    <div className="bns--card-head">
                        <ChipIcon />
                        {t("bns.totalBets")}
                    </div>
                    <div className="bns--card-value">{utils.convertor(bonus, "USD", 1)}</div>
                    <div className="bns--card-note">{t("bns.jackpotShare")} {utils.convertor(totals.jackpot, "USD", 1)}</div>
                </div>

                {/* Wins */}
                <div className="bns--card win">
                    <div className="bns--card-head">
                        <TrophyIcon />
                        {t("bns.totalWins")}
                    </div>
                    <div className="bns--card-value">{utils.convertor(win, "USD", 1)}</div>
                    <div className="bns--card-note">{formatCount(stats.wins)} {t("bns.summaryCount")}</div>
                </div>

                {/* Net */}
                <div className={`bns--card ${net >= 0 ? "positive" : "negative"}`}>
                    <div className="bns--card-head">
                        <NetIcon />
                        {t("bns.totalNet")}
                    </div>
                    <div className="bns--card-value">{utils.convertor(net, "USD", 1)}</div>
                    <div className="bns--card-note">{net >= 0 ? t("bns.netPositive") : t("bns.netNegative")}</div>
                </div>

            </div>
        )
    }

    /* Draw Content */
    _content = () => {

        /* Fields */
        const { status, data, count, search } = this.state
        const { navigate } = this.props
        const page = this.getPage()

        /* Check Status */
        if (status !== "ok") {
            return (
                <div className="bns--page">
                    <InnerLayout inner status={status} reload={() => this.load(true)} />
                </div>
            )
        }

        return (
            <div className="bns--page">

                {/* Head */}
                <div className="bns--table-header">
                    <div className="bns--column bns--col-number">#</div>
                    <div className="bns--column bns--col-game">{t("bns.game")}</div>
                    <div className="bns--column bns--col-player">{t("bns.player")}</div>
                    <div className="bns--column bns--col-bet">{t("bns.bet")}</div>
                    <div className="bns--column bns--col-win">{t("bns.win")}</div>
                    <div className="bns--column bns--col-pool">{t("bns.jackpot")}</div>
                    <div className="bns--column bns--col-date">{t("bns.date")}</div>
                    <div className="bns--column bns--col-actions" />
                </div>

                {/* Empty */}
                {data.length === 0 &&
                    <div className="bns--empty">
                        <GiftIcon size={40} />
                        <span>{search !== "" || this.getCount() > 0 ? t("bns.nothingFound") : t("bns.empty")}</span>
                    </div>
                }

                {/* Body */}
                {data.map((item, index) =>
                    <div onClick={() => this._detailModal.open(item)} className={`bns--row ${isWon(item) ? "win" : ""}`} key={`${index}`}>

                        {/* Number */}
                        <div className="bns--column bns--col-number">{(page - 1) * SIZE + index + 1}</div>

                        {/* Game */}
                        <div className="bns--column bns--col-game">

                            <div className={`bns--icon ${isWon(item) ? "win" : ""}`}>
                                {isWon(item) ? <TrophyIcon size={18} /> : <ChipIcon size={18} />}
                            </div>

                            <div className="bns--game-text">

                                <div className="bns--game-name">{getGame(item)}</div>

                                <div className="bns--game-note">
                                    <StudioIcon size={11} />
                                    {utils.checkValue(item.studio, "—")}
                                </div>

                                {item.gameNumber &&
                                    <div className="bns--game-note">
                                        <HashIcon size={11} />
                                        {item.gameNumber}
                                    </div>
                                }

                            </div>

                        </div>

                        {/* Player */}
                        <div className="bns--column bns--col-player">

                            <Avatar uri={getAvatar(item)} size={38} />

                            <div className="bns--player-text">

                                <div className="bns--player-name">
                                    <span className="bns--player-value">{getName(item)}</span>
                                    {parseInt(item.isTester) === 1 && <span className="bns--tag tester">{t("bns.tester")}</span>}
                                    {parseInt(item.isVip) === 1 &&
                                        <span className="bns--tag vip">
                                            <VipIcon size={10} />
                                            VIP
                                        </span>
                                    }
                                </div>

                                <div className="bns--player-code">{item.player}</div>

                                <div className="bns--operator" title={utils.checkValue(item.operator, "")}>
                                    <OperatorIcon size={11} />
                                    <span>{utils.checkValue(item.operator, "—")}</span>
                                </div>

                            </div>

                        </div>

                        {/* Bet */}
                        <div className="bns--column bns--col-bet">

                            <div className="bns--total">{utils.convertor(item.dollarBonus, "USD", 1)}</div>

                            {getCurrency(item.currency) !== "USD" &&
                                <div className="bns--local">{utils.convertor(item.bonus, getCurrency(item.currency), 1)}</div>
                            }

                        </div>

                        {/* Win */}
                        <div className="bns--column bns--col-win">

                            {isWon(item)
                                ? <>
                                    <div className="bns--total win">{utils.convertor(getWinUSD(item), "USD", 1)}</div>

                                    {getCurrency(item.currency) !== "USD" &&
                                        <div className="bns--local">{utils.convertor(item.win, getCurrency(item.currency), 1)}</div>
                                    }

                                    {item.combination &&
                                        <div className="bns--combination">
                                            <CardsIcon size={11} />
                                            {item.combination}
                                            {item.multiplier ? <span className="bns--multiplier">{item.multiplier} : 1</span> : null}
                                        </div>
                                    }
                                </>
                                : <div className="bns--total empty">—</div>
                            }

                        </div>

                        {/* Jackpot */}
                        <div className="bns--column bns--col-pool">

                            <div className="bns--pool-value">{utils.convertor(item.jackpot, "USD", 1)}</div>
                            <div className="bns--pool-share">+ {utils.convertor(getJackpotPart(item), "USD", 1)}</div>

                        </div>

                        {/* Date */}
                        <div className="bns--column bns--col-date">
                            <div className="bns--date">{formatDay(item.createdAt)}</div>
                            <div className="bns--time">{formatTime(item.createdAt)}</div>
                            <div className="bns--ago">{formatAgo(item.createdAt)}</div>
                        </div>

                        {/* Actions */}
                        <div onClick={(event) => event.stopPropagation()} className="bns--column bns--col-actions">

                            <div onClick={() => this._detailModal.open(item)} className="bns--action" title={t("bns.detailTitle")}>
                                <EyeIcon />
                            </div>

                            <div onClick={() => this.copy(item.gameNumber)} className="bns--action" title={t("bns.copyNumber")}>
                                <CopyIcon size={15} />
                            </div>

                        </div>

                    </div>
                )}

                {/* Pagination */}
                <Pagination size={SIZE} page={page} total={count} onPageChange={(page) => navigate(`/bonus/${page}`)} />

            </div>
        )
    }

    render = () => {

        /* Fields */
        const { status, settings } = this.state
        const { socket } = this.props

        return (
            <div className="page">

                <div className="page-header bns--header">

                    <h1>{t("bns.title")}</h1>

                    {status === "ok" &&
                        <div className="bns--tools">

                            {/* Draw Search */}
                            {this._search()}

                            {/* Draw Filter Button */}
                            {this._filter()}

                            {/* Draw Settings Button */}
                            {this._settings()}

                        </div>
                    }

                </div>

                {/* Draw Numbers */}
                {status === "ok" && this._numbers()}

                {/* Draw Money */}
                {status === "ok" && this._summary()}

                {/* Draw Content */}
                {this._content()}

                {/* Draw Detail Modal */}
                <Detail ref={(ref) => this._detailModal = ref} />

                {/* Draw Filter Modal */}
                <Filter apply={this.applyFilter} ref={(ref) => this._filterModal = ref} />

                {/* Draw Jackpot Modal — only the superadmin ever mounts it */}
                {settings && this.isSuperAdmin() &&
                    <JackpotModal
                        socket={socket}
                        visible={settings}
                        close={() => this.setState({ settings: false })}
                    />
                }

            </div>
        )
    }

}

const mapStateToProps = (state) => ({
    admin: state.admin,
    language: state.language,
})

export default connect(mapStateToProps)(withRouter(Bonus))
